import type {
  MobileGraphqlEndpoint,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { resolveNestedColumns, type ResolvedArtifactColumns } from './columns';
import { ARTIFACT_ID_FIELD } from './constants';
import { isGraphQLName, toGraphQLLiteral } from './gql-literal';

/** Inputs for one page of an artifact list query. */
export interface ArtifactListArgs {
  mobileAcquisitionId: number;
  offset: number;
  limit: number;
  /** Prisma-style ``where`` object, already compiled from column filters. */
  where?: Record<string, unknown> | null;
  /** ``{ field, direction }`` with direction as a GraphQL enum literal. */
  orderBy?: Record<string, unknown> | null;
}

export interface BuiltArtifactQuery {
  query: string;
  endpoint: MobileGraphqlEndpoint;
  rootQuery: string;
}

function buildSelectionSet(
  resolved: ResolvedArtifactColumns,
  uiConfig: MobileUiConfigResponse,
): string {
  const parts: string[] = [];
  const emitted = new Set<string>();

  const emitScalar = (name: string) => {
    if (!name || !isGraphQLName(name) || emitted.has(name)) return;
    emitted.add(name);
    parts.push(name);
  };

  // The id field is guaranteed to exist (chosen from the type's fields) and is
  // used as the stable row key.
  emitScalar(resolved.idField);

  for (const col of resolved.columns) {
    if (col.nested) {
      const child = col.nested.graphql_type
        ? uiConfig.artifacts[col.nested.graphql_type]
        : undefined;
      const childHasId = child?.fields.some((f) => f.name === ARTIFACT_ID_FIELD) ?? false;
      const sub = resolveNestedColumns(col.nested, uiConfig);
      const subNames = new Set<string>([
        ...(childHasId ? [ARTIFACT_ID_FIELD] : []),
        ...sub.map((c) => c.field),
      ]);
      const subSel = [...subNames].filter((name) => isGraphQLName(name)).join(' ');
      if (subSel && isGraphQLName(col.field)) parts.push(`${col.field} { ${subSel} }`);
      continue;
    }
    // Skip object/list scalars we can't select without a sub-selection.
    if (col.meta && (col.meta.object || col.meta.list)) continue;
    // A curated default_columns entry that isn't on the GraphQL type would
    // make the whole document invalid (`Cannot query field X`). Drop it.
    if (!col.meta) continue;
    emitScalar(col.field);
  }

  for (const col of resolved.contentFields) {
    if (col.meta && (col.meta.object || col.meta.list)) continue;
    if (!col.meta) continue;
    emitScalar(col.field);
  }

  return parts.join('\n        ');
}

/**
 * Build a dynamic GraphQL list query for an artifact type. Argument values are
 * inlined as GraphQL literals (see {@link toGraphQLLiteral}) so the builder
 * needs no per-type input type names.
 *
 * Injection safety: ``where`` keys are GraphQL names intersected with
 * ``capabilities.filterable_fields`` in {@link compileWhere} before they reach
 * this builder, because the URL can carry arbitrary field names. ``toGraphQLLiteral``
 * also drops any key that is not a GraphQL name. User-supplied filter *values*
 * are JSON-escaped. Selection-set segments that are not GraphQL names are omitted.
 */
export function buildArtifactListQuery(
  resolved: ResolvedArtifactColumns,
  endpoint: MobileGraphqlEndpoint,
  uiConfig: MobileUiConfigResponse,
  args: ArtifactListArgs,
): BuiltArtifactQuery | null {
  if (!resolved.rootQuery || !isGraphQLName(resolved.rootQuery)) return null;

  const argParts: string[] = [
    `mobileAcquisitionId: ${args.mobileAcquisitionId}`,
    `offset: ${args.offset}`,
    `limit: ${args.limit}`,
  ];
  if (args.where && Object.keys(args.where).length > 0) {
    argParts.push(`where: ${toGraphQLLiteral(args.where)}`);
  }
  if (args.orderBy && Object.keys(args.orderBy).length > 0) {
    argParts.push(`orderBy: ${toGraphQLLiteral(args.orderBy)}`);
  }

  const selection = buildSelectionSet(resolved, uiConfig);

  const query = `query ArtifactList {
  ${resolved.rootQuery}(${argParts.join(', ')}) {
    total
    offset
    limit
    items {
        ${selection}
    }
  }
}`;

  return { query, endpoint, rootQuery: resolved.rootQuery };
}
